/// <amd-module name="Build/Admin/PoolsList/Components/CreateElasticPoolContainer" />
import * as React from "react";

import { ServiceEndpoint } from "ServiceEndpoints/Client/Contracts/ServiceEndpoint";

import { IProjectData } from "Tfs/Platform/Page";
import { ITeamProject } from "TFS/Services/TfsProjectService";

import { ElasticPool, OperatingSystemType, ResourceLimit } from "DistributedTask/Client/Contracts/TaskAgent";

import { IVssComponentContext, VssComponent } from "VSS/Platform/Layout";

import { IButtonProps } from "VSSUI/Button";
import { ConditionalChildren } from "VSSUI/ConditionalChildren";

import { VirtualMachineScaleSet } from "Build/Admin/Common/Client/Contracts/DistributedTask/Azure";
import { ElasticPoolSettings } from "Build/Admin/Common/Components/ElasticPoolSettings";
import { ProjectPicker } from "Build/Admin/Common/Components/ProjectPicker";
import { VMScaleSetSettings } from "Build/Admin/Common/Components/VMScaleSetSettings";
import { getVMScaleSetOSType } from "Build/Admin/Common/ElasticPoolUtils";
import { IProjectDataService } from "Build/Admin/Common/Services/ProjectDataService";
import { PoolsListActionCreator } from "Build/Admin/PoolsList/Actions/PoolsListActionCreator";
import { PipelinePermissionsSettings } from "Build/Admin/PoolsList/Components/PipelinePermissionsSettings";
import { PoolNameDescriptionSettings } from "Build/Admin/PoolsList/Components/PoolNameDescriptionSettings";
import { StoresHub } from "Build/Admin/PoolsList/Stores/StoresHub";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";

import * as Resources from "Build/Admin/PoolsList/Resources";

export interface ICreateElasticPoolContainerProps {
    actionCreator: PoolsListActionCreator;
    storesHub: StoresHub;
    onFooterButtonPropsChanged(footerButtonProps: IButtonProps[]): void;
}

interface ICreateElasticPoolContainerState {
    projectId?: string;
    poolName?: string;
    poolDescription?: string;
    authorizeAllPipelines: boolean;
    autoProvisionProjectPools: boolean;
    serviceEndpoint?: ServiceEndpoint;
    vmScaleSet?: VirtualMachineScaleSet;
    recycleAfterEachUse: boolean;
    maxSavedNodeCount: number;
    maxCapacity?: number;
    desiredIdle?: number;
    timeToLiveMinutes?: number;
    agentInteractiveUI: boolean;
    disableSubmitButton: boolean;
    operatingSystem: OperatingSystemType | undefined;
    resourceLimits: ResourceLimit[];
}

export class CreateElasticPoolContainer extends VssComponent<ICreateElasticPoolContainerProps, ICreateElasticPoolContainerState> {
    private _project: IProjectData | undefined;
    private _disableAllPipelinesSetting: boolean;
    private _showGrantAccessPermissionToAllPipelines: boolean;

    constructor(props: ICreateElasticPoolContainerProps, context: IVssComponentContext) {
        super(props, context);

        const projectDataService: IProjectDataService = context.pageContext.getService<IProjectDataService>("IProjectDataService");
        this._project = projectDataService.getProjectData();

        this._disableAllPipelinesSetting = !this.props.storesHub.securityStore.state.hasManageAllPermission;
        this._showGrantAccessPermissionToAllPipelines = isFeatureFlagEnabled(this.context.pageContext, "DistributedTask.EnableGrantOrgLevelAccessPermissionToAllPipelinesInAgentPools", false);

        this.state = this._getStateFromStores();
    }

    public componentDidMount(): void {
        this.props.onFooterButtonPropsChanged(this._getFooterButtonProps());
        this.props.storesHub.resourceLimitStore.addChangedListener(this._onStoreChanged);
    }

    public componentDidUpdate(prevProps: ICreateElasticPoolContainerProps, prevState: ICreateElasticPoolContainerState): void {
        if (prevState.disableSubmitButton !== this.state.disableSubmitButton) {
            this.props.onFooterButtonPropsChanged(this._getFooterButtonProps());
        }
    }

    public componentWillUnmount(): void {
        this.props.storesHub.resourceLimitStore.removeChangedListener(this._onStoreChanged);
    }

    public render(): JSX.Element {
        var poolIsCreatedInProjectLevel = this._project !== undefined;

        return (
            <div className="create-elastic-pool-container">
                <ConditionalChildren renderChildren={this._project === undefined}>
                    <div className="pool-setting-label">
                        {Resources.SelectProjectHeader}
                    </div>
                    <ProjectPicker projects={this.props.storesHub.projectStore.state} onProjectSelected={this._onProjectSelected} />
                </ConditionalChildren>
                <VMScaleSetSettings
                    actionCreator={this.props.actionCreator.serviceEndpointActionCreator}
                    serviceEndpointStore={this.props.storesHub.serviceEndpointStore}
                    virtualMachineScaleSetStore={this.props.storesHub.virtualMachineScaleSetStore}
                    projectId={this.state.projectId}
                    onSettingsChanged={this._onVMScaleSetSettingsChanged} />
                <PoolNameDescriptionSettings
                    poolName={this.state.poolName}
                    poolDescription={this.state.poolDescription}
                    onSettingsChanged={this._onPoolNameDescriptionChanged} />
                <div className="pool-setting-label">
                    {Resources.ElasticPoolSettings}
                </div>
                <ElasticPoolSettings
                    showSaveFailedAgentCheckbox={false}
                    recycleAfterEachUse={this.state.recycleAfterEachUse}
                    saveFailedAgents={this.state.maxSavedNodeCount > 0}
                    maxCapacity={this.state.maxCapacity}
                    desiredIdle={this.state.desiredIdle}
                    timeToLiveMinutes={this.state.timeToLiveMinutes}
                    agentInteractiveUI={this.state.agentInteractiveUI}
                    operatingSystem={this.state.operatingSystem}
                    resourceLimits={this.state.resourceLimits}
                    virtualMachineScaleSet={this.state.vmScaleSet}
                    onSettingsChanged={this._onElasticPoolSettingsChanged} />
                <PipelinePermissionsSettings
                    authorizeAllPipelines={this.state.authorizeAllPipelines}
                    autoProvisionProjectPools={this.state.autoProvisionProjectPools}
                    showAuthorizeAllPipelinesOption={poolIsCreatedInProjectLevel || this._showGrantAccessPermissionToAllPipelines}
                    showAutoProvisionOption={!poolIsCreatedInProjectLevel}
                    disableAllPipelinesSetting={this._disableAllPipelinesSetting}
                    onSettingsChanged={this._onPermissionsChanged} />
            </div>
        );
    }

    private _onProjectSelected = (project: ITeamProject | undefined): void => {
        this.setState({
            projectId: project ? project.id : undefined,
            serviceEndpoint: undefined,
            vmScaleSet: undefined,
            disableSubmitButton: this._shouldDisableSubmitButton({
                    ...this.state,
                    serviceEndpoint: undefined,
                    vmScaleSet: undefined
                })
        });
    }

    private _onVMScaleSetSettingsChanged = (serviceEndpoint: ServiceEndpoint | undefined, vmScaleSet: VirtualMachineScaleSet | undefined): void => {
        const operatingSystem: OperatingSystemType | undefined = getVMScaleSetOSType(vmScaleSet);
        this.setState({
            serviceEndpoint,
            vmScaleSet,
            operatingSystem,
            disableSubmitButton: this._shouldDisableSubmitButton({
                    ...this.state,
                    serviceEndpoint,
                    vmScaleSet
                })
        });
    }

    private _onPoolNameDescriptionChanged = (poolName: string | undefined, poolDescription: string | undefined): void => {
        this.setState({
            poolName,
            poolDescription,
            disableSubmitButton: this._shouldDisableSubmitButton({
                ...this.state,
                poolName
            })
        });
    }

    private _onElasticPoolSettingsChanged = (recycleAfterEachUse: boolean, saveFailedAgents: boolean, maxCapacity: number | undefined, desiredIdle: number | undefined, timeToLiveMinutes: number | undefined, agentInteractiveUI: boolean): void => {
        this.setState({
            recycleAfterEachUse,
            maxSavedNodeCount: saveFailedAgents ? 1 : 0,
            maxCapacity,
            desiredIdle,
            timeToLiveMinutes,
            agentInteractiveUI,
            disableSubmitButton: this._shouldDisableSubmitButton({
                ...this.state,
                recycleAfterEachUse,
                maxSavedNodeCount: saveFailedAgents ? 1 : 0,
                maxCapacity,
                desiredIdle,
                timeToLiveMinutes,
                agentInteractiveUI
            })
        });
    }

    private _onPermissionsChanged = (authorizeAllPipelines: boolean, autoProvisionProjectPools: boolean): void => {
        this.setState({
            authorizeAllPipelines,
            autoProvisionProjectPools,
            disableSubmitButton: this._shouldDisableSubmitButton(this.state)
        });
    }

    private _onStoreChanged = (): void => {
        this.setState(this._getStateFromStores());
    }

    private _getStateFromStores = (): ICreateElasticPoolContainerState => {
        // This is used on panel initialization, and to reset the panel state on open/close
        // so some of the panel's fields are set to their default values below.
        // The panel's fields are set by setState calls in the child component callbacks in the render function.
        return {
            projectId: this._project ? this._project.id : undefined,
            poolName: undefined,
            poolDescription: undefined,
            authorizeAllPipelines: false,
            autoProvisionProjectPools: this._project === undefined,
            serviceEndpoint: undefined,
            vmScaleSet: undefined,
            recycleAfterEachUse: false,
            maxSavedNodeCount: 0,
            maxCapacity: undefined,
            desiredIdle: undefined,
            timeToLiveMinutes: 30,
            agentInteractiveUI: false,
            disableSubmitButton: true,
            operatingSystem: undefined,
            resourceLimits: this.props.storesHub.resourceLimitStore.state.resourceLimits
        };
    }

    private _okOnClick = (): void => {
        const elasticPool: ElasticPool = {
            agentInteractiveUI: this.state.agentInteractiveUI,
            azureId: this.state.vmScaleSet?.id,
            desiredIdle: this.state.desiredIdle,
            maxCapacity: this.state.maxCapacity,
            osType: this.state.operatingSystem,
            maxSavedNodeCount: this.state.maxSavedNodeCount,
            recycleAfterEachUse: this.state.recycleAfterEachUse,
            serviceEndpointId: this.state.serviceEndpoint?.id,
            serviceEndpointScope: this.state.projectId,
            timeToLiveMinutes: this.state.timeToLiveMinutes
        } as ElasticPool;
        this.props.actionCreator.addElasticPool(elasticPool, this.state.poolName!, this.state.authorizeAllPipelines, this.state.autoProvisionProjectPools, this._project?.id, this.state.poolDescription);
        this.props.actionCreator.dismissCreatePoolPanel();
    }

    private _shouldDisableSubmitButton = (newState: ICreateElasticPoolContainerState): boolean => {
        return !newState.poolName ||
            !newState.serviceEndpoint ||
            !newState.vmScaleSet ||
            newState.maxCapacity === undefined ||
            newState.maxCapacity < 0 ||
            newState.maxCapacity > 1000 ||
            newState.desiredIdle === undefined ||
            newState.desiredIdle > newState.maxCapacity ||
            newState.timeToLiveMinutes === undefined ||
            newState.timeToLiveMinutes < 0 ||
            newState.timeToLiveMinutes > 10000;
    }

    private _getFooterButtonProps = (): IButtonProps[] => {
        return [
            {
                text: Resources.Create,
                primary: true,
                onClick: this._okOnClick,
                disabled: this.state.disableSubmitButton
            }
        ];
    }
}
